<template>
    <div class="row">
        <div v-if="!selectedPod" class="col-12">
            <pod-selector @select="podSelected" />
        </div>

        <div v-if="podComponentName && filteredTemplateProductConfiguration" class="col-12">
            <div v-show="!savingVariants">
                <component
                    :is="podComponentName"
                    :store="storeUrl"
                    :initial-configuration="filteredTemplateProductConfiguration"
                    @save="onConfigurationSaved"
                />
            </div>

            <div v-if="savingVariants" class="col-12 align-items-center d-flex flex-column justify-content-center p-4">
                <b-spinner variant="primary" class="mr-2" />
                <div class="mt-2">Saving configuration</div>
            </div>

            <div class="col-12 text-center mb-3 mt-3" style="color: red">
                <b>{{ errorMessage }}</b>
            </div>
        </div>

        <div v-if="selectedPod && !savingVariants" class="col-12">
            <button class="btn btn-secondary float-left" @click="goToPodSelection">Go to POD selection</button>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { Component, Ref, computed, onBeforeMount, ref } from 'vue';
import { getTemplateProduct } from '@/api/admin/templateProducts';
import { saveTemplateProduct } from '@/api/templateProduct';
import { Provider } from '@/utils/PodProvider.enum';
import { TemplateProductConfiguration } from './typings';
import { TemplateProduct, PodAssignment } from '@/api/typings/templateProducts';
import PrintfulConfigurator from './PrintfulConfigurator/PrintfulConfigurator.vue';
import PrintifyConfigurator from './PrintifyConfigurator/PrintifyConfigurator.vue';
import PodSelector from '../PodSelector/PodSelector.vue';

type PodKey = 'printify' | 'printful';

const props = defineProps<{
    templateProductId?: string;
}>();

const emit = defineEmits(['save']);

const selectedPod: Ref<PodKey | null> = ref(null);
let selectedPodId: number | null = null;
const storeUrl = ref('');
const errorMessage = ref('');
const savingVariants = ref(false);
let templateProductConfiguration: TemplateProductConfiguration | null = null;

const podComponentName = computed(() => {
    const components: Record<PodKey, Component> = {
        [Provider.PRINTIFY]: PrintifyConfigurator,
        [Provider.PRINTFUL]: PrintfulConfigurator
    };
    return selectedPod.value ? components[selectedPod.value] : null;
});

const filteredTemplateProductConfiguration = computed((): TemplateProductConfiguration | null => {
    if (selectedPod.value) {
        const templateProductConfig: TemplateProductConfiguration = JSON.parse(
            JSON.stringify(templateProductConfiguration)
        );
        templateProductConfig.configurations = templateProductConfig.configurations.filter(
            (c) => c.provider === selectedPod.value
        );
        return templateProductConfig;
    }

    return templateProductConfiguration;
});

onBeforeMount(() => {
    buildTemplateProductConfiguration();
});

const buildTemplateProductConfiguration = async (templateProduct?: TemplateProduct) => {
    const templateProductsConfiguration: TemplateProductConfiguration = {
        templateProductId: props.templateProductId,
        configurations: []
    };

    if (templateProduct || props.templateProductId) {
        templateProduct = templateProduct || (await getTemplateProduct(props.templateProductId!));
        if (selectedPodId) templateProduct.podId = selectedPodId;
        for (const podAssignment of templateProduct.podAssignments) {
            const currentConfig = templateProductsConfiguration.configurations.find(
                (config) => config.productId === podAssignment.productId && podAssignment.podService === config.provider
            );

            // Add the product if it doesn't exists
            if (!currentConfig) {
                templateProductsConfiguration.configurations.push({
                    productId: podAssignment.productId,
                    provider: podAssignment.podService,
                    placeholders: templateProduct.podAssignments
                        .filter((assignment) => assignment.productId === podAssignment.productId)
                        .map((assignment) => ({
                            printProviderId: assignment.printProviderId,
                            variantId: assignment.variantId,
                            placeholderId: assignment.placeholderId
                        }))
                });
                continue;
            }

            currentConfig.placeholders.push({
                variantId: podAssignment.variantId,
                placeholderId: podAssignment.placeholderId
            });
        }
    }

    templateProductConfiguration = templateProductsConfiguration;
};

const podSelected = (data: { pod: PodKey | null; storeUrl: string; podId: number }) => {
    selectedPod.value = data.pod;
    storeUrl.value = data.storeUrl;
    selectedPodId = data.podId;
    if (!selectedPod.value) initializeManualTemplateProduct();
};

const goToPodSelection = () => {
    selectedPod.value = null;
    selectedPodId = null;
};

const initializeManualTemplateProduct = () => {
    emit('save', {
        templateProduct: null,
        podAssignments: [],
        storeUrl: storeUrl.value,
        biggestPlaceholder: null,
        podId: selectedPodId
    });
};

const onConfigurationSaved = async (data: {
    configuration: TemplateProductConfiguration;
    biggestPlaceholders: { width: number; height: number; id: string }[];
}) => {
    try {
        errorMessage.value = '';
        savingVariants.value = true;

        let podAssignments: PodAssignment[] = [];
        let templateProduct: TemplateProduct | null = null;
        if (props.templateProductId) {
            templateProduct = await getTemplateProduct(props.templateProductId);
            if (selectedPodId) templateProduct.podId = selectedPodId;
            podAssignments = templateProduct.podAssignments;
        }

        // Filter-out all the configurations from the selected provider
        podAssignments = podAssignments.filter((v) => v.podService !== selectedPod.value);

        // Add all the new configurations
        for (const configuration of data.configuration.configurations) {
            for (const placeholder of configuration.placeholders) {
                podAssignments.push({
                    id: 0,
                    productId: configuration.productId,
                    podService: configuration.provider,
                    placeholderId: placeholder.placeholderId,
                    variantId: placeholder.variantId,
                    printProviderId: placeholder.printProviderId
                });
            }
        }

        // Only save the template if it's available
        if (templateProduct) {
            templateProduct.podAssignments = podAssignments;
            if (selectedPodId) templateProduct.podId = selectedPodId;
            const savedTemplate = await saveTemplateProduct(templateProduct, templateProduct.id);
            buildTemplateProductConfiguration(savedTemplate);
        }

        emit('save', {
            templateProduct: templateProduct,
            podAssignments: podAssignments,
            storeUrl: storeUrl.value,
            biggestPlaceholders: data.biggestPlaceholders,
            podId: selectedPodId
        });
        goToPodSelection();
    } catch (e) {
        errorMessage.value = 'Error saving variants';
        throw e;
    } finally {
        savingVariants.value = false;
    }
};
</script>
